package com.fantasytenis.web.components.skills

import androidx.compose.runtime.Composable
import com.fantasytenis.shared.domain.skills.entity.SkillTrend
import com.fantasytenis.shared.domain.skills.entity.TrendDirection
import com.fantasytenis.web.theme.Colors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Trend indicator showing up/down/stable with delta
 *
 * @param trend The skill trend to display
 * @param showDelta Whether to show the delta value
 * @param size Font size in pixels
 */
@Composable
fun TrendIndicator(
    trend: SkillTrend,
    showDelta: Boolean = true,
    size: Int = 14
) {
    val (icon, color) = when (trend.direction) {
        TrendDirection.UP -> Pair("↑", Colors.Success)
        TrendDirection.DOWN -> Pair("↓", Colors.Error)
        TrendDirection.STABLE -> Pair("—", Colors.TextSecondary)
    }

    Span({
        style {
            property("display", "inline-flex")
            alignItems(AlignItems.Center)
            gap(2.px)
            fontSize(size.px)
            fontWeight(600)
            color(color)
        }
    }) {
        // Arrow/dash icon
        Span {
            Text(icon)
        }

        // Delta value (optional)
        if (showDelta && trend.delta > 0) {
            Span {
                val prefix = if (trend.direction == TrendDirection.UP) "+" else "-"
                Text("$prefix${trend.delta}")
            }
        }
    }
}

/**
 * Compact trend indicator (just the arrow)
 */
@Composable
fun TrendArrow(
    trend: SkillTrend,
    size: Int = 12
) {
    val (icon, color) = when (trend.direction) {
        TrendDirection.UP -> Pair("▲", Colors.Success)
        TrendDirection.DOWN -> Pair("▼", Colors.Error)
        TrendDirection.STABLE -> Pair("—", Colors.TextSecondary)
    }

    Span({
        style {
            fontSize(size.px)
            color(color)
        }
    }) {
        Text(icon)
    }
}

/**
 * Trend badge with background
 */
@Composable
fun TrendBadge(
    trend: SkillTrend,
    size: Int = 12
) {
    val (bgColor, textColor, prefix) = when (trend.direction) {
        TrendDirection.UP -> Triple(Colors.SuccessLight, Colors.SuccessDark, "+")
        TrendDirection.DOWN -> Triple(Colors.ErrorLight, Colors.ErrorDark, "-")
        TrendDirection.STABLE -> Triple(Colors.Gray200, Colors.TextSecondary, "")
    }

    Span({
        style {
            property("display", "inline-flex")
            alignItems(AlignItems.Center)
            gap(2.px)
            backgroundColor(bgColor)
            color(textColor)
            fontSize(size.px)
            fontWeight(600)
            padding(2.px, 6.px)
            borderRadius(4.px)
        }
    }) {
        when (trend.direction) {
            TrendDirection.UP -> Text("↑ +${trend.delta}")
            TrendDirection.DOWN -> Text("↓ -${trend.delta}")
            TrendDirection.STABLE -> Text("—")
        }
    }
}
